import MarkdownIt from 'markdown-it'
import hljs from 'highlight.js/lib/core'
import markdownItHighlightjs from 'markdown-it-highlightjs'

// 載入常用語言支援
import javascript from 'highlight.js/lib/languages/javascript'
import css from 'highlight.js/lib/languages/css'
import html from 'highlight.js/lib/languages/xml'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import bash from 'highlight.js/lib/languages/bash'

// 註冊語言
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('css', css)
hljs.registerLanguage('html', html)
hljs.registerLanguage('xml', html) // HTML 的別名
hljs.registerLanguage('python', python)
hljs.registerLanguage('ruby', ruby)
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('shell', bash) // bash 的別名

// 建立 MarkdownIt 實例
const md = new MarkdownIt({
  html: true,        // 允許 HTML 標籤
  xhtmlOut: false,   // 使用 HTML 輸出（更相容）
  breaks: true,      // 自動轉換換行符為 <br>，保持使用者的換行習慣
  linkify: true,     // 自動將網址轉換為連結
  typographer: true, // 啟用智慧標點符號
})

// 使用語法高亮外掛
md.use(markdownItHighlightjs, { hljs })

/**
 * 將 markdown 文字轉換為 HTML
 * @param {string} content - 要轉換的 markdown 內容
 * @returns {string} 轉換後的 HTML 字串
 */
export function parseMarkdown(content) {
  if (!content) return ''
  
  try {
    return md.render(content)
  } catch (error) {
    console.error('Markdown 解析錯誤:', error)
    // 如果解析失敗，返回原始文字（用 <p> 包裹以保持格式）
    return `<p>${content}</p>`
  }
}

/**
 * 檢查文字是否包含 markdown 語法
 * @param {string} content - 要檢查的內容
 * @returns {boolean} 是否包含 markdown 語法
 */
export function hasMarkdownSyntax(content) {
  if (!content) return false
  
  // 檢查常見的 markdown 語法標記（按優先級排序）
  const markdownPatterns = [
    /^#{1,6}\s/m,           // 標題 # ## ###
    /^\s*[-*+]\s/m,         // 無序列表 - * + 
    /^\s*\d+\.\s/m,         // 有序列表 1. 2.
    /\*\*.*?\*\*/,          // 粗體 **text**
    /(?<!\*)\*(?!\*)([^*\n]+)\*(?!\*)/,  // 斜體 *text* （避免與列表符號衝突）
    /`.*?`/,                // 行內程式碼 `code`
    /```[\s\S]*?```/,       // 程式碼區塊 ```code```
    /^\s*>/m,               // 引用 >
    /\[.*?\]\(.*?\)/,       // 連結 [text](url)
    /!\[.*?\]\(.*?\)/,      // 圖片 ![alt](url)
    /^\s*\|.*\|/m,          // 表格 |col1|col2|
    /^-{3,}$/m,             // 分隔線 ---
  ]
  
  return markdownPatterns.some(pattern => pattern.test(content))
}

/**
 * 為了向後相容，如果內容沒有 markdown 語法，就將換行符轉換為 <br>
 * @param {string} content - 原始內容
 * @returns {string} 處理後的 HTML
 */
/**
 * 處理 @ 提及和 URL 連結
 */
function processLinks(content) {
  return content
    // 處理 @ 提及（簡化版，只加粗體）
    .replace(/@([a-zA-Z0-9_]+)/g, '<strong>@$1</strong>')
    // 處理 URL 連結
    .replace(/https?:\/\/[\S]+/g, '<a href="$&" target="_blank">$&</a>')
}

export function processContent(content) {
  if (!content) return ''

  // 先標準化換行符：將 \r\n 和 \r 統一轉換為 \n
  content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

  if (hasMarkdownSyntax(content)) {
    // 如果包含 markdown 語法，使用更可靠的方法來保留空白行
    // 1. 將所有只包含空白的行，替換成一個獨特的 HTML 註釋標記
    //    `^` 和 `$` 確保我們匹配一整行
    //    `\s*` 匹配零個或多個空白字元 (空格, tab)
    const processedContent = content.replace(/^(\s*)$/gm, '<!-- BLANK_LINE -->')

    // 2. 使用 MarkdownIt 解析
    let html = parseMarkdown(processedContent)

    // 3. 將 HTML 註釋標記替換為實際的空白段落
    //    g 旗標確保替換所有出現的標記
    html = html.replace(/<!-- BLANK_LINE -->/g, '<p class="blank-line">&nbsp;</p>')

    return html
  } else {
    // 對於沒有 markdown 語法的舊內容，保持原有處理方式
    let result = content.replace(/\n/g, '<br>')
    result = processLinks(result)
    return result
  }
}

export default {
  parseMarkdown,
  hasMarkdownSyntax,
  processContent,
  processLinks
}